// "More filters" for the leads list: any filterable lead field with its operators.
//
// The field list and each field's operators come from the backend's column
// registry (GET /api/view-preferences/columns → config/leadColumns.js), and the
// result is sent as GET /api/leads?filters=[{ column, operator, value, value2 }].
// The backend drops anything it doesn't know, so only registry fields and
// operators are offered. Status values are the clinic's own stages.
import { useEffect, useMemo, useState } from "react";
import { Plus, SlidersHorizontal, X } from "lucide-react";
import { useOutside } from "./ui";

const sel = "h-10 min-w-0 rounded-sm border border-line bg-paper px-2.5 text-small text-ink outline-none hover:border-muted/50 focus:border-ink focus:ring-2 focus:ring-ink/10";
const NO_VALUE = new Set(["is_empty", "not_empty"]);

/** Plain-language label of one filter, for chips: "UTM campaign is diwali". */
export function filterLabel(f, catalog, statuses = []) {
  const col = catalog.find((c) => c.key === f.column);
  if (!col) return f.column;
  const op = col.operators?.find((o) => o.value === f.operator)?.label || f.operator;
  const name = (v) => (col.key === "status" ? statuses.find((s) => s.key === v)?.name : col.options?.find((o) => o.value === v)?.label) || v;
  const value = NO_VALUE.has(f.operator) ? "" : Array.isArray(f.value) ? f.value.map(name).join(", ") : f.operator === "between" ? `${f.value} and ${f.value2}` : name(f.value);
  return `${col.label} ${op}${value ? ` ${value}` : ""}`;
}

/** A filter the backend will accept (it drops the rest silently). */
function complete(f, catalog) {
  const col = catalog.find((c) => c.key === f.column);
  if (!col || !col.operators?.some((o) => o.value === f.operator)) return false;
  if (NO_VALUE.has(f.operator)) return true;
  if (f.operator === "in") return Array.isArray(f.value) && f.value.length > 0;
  if (f.operator === "between") return !!f.value && !!f.value2;
  return f.value !== undefined && f.value !== null && String(f.value).trim() !== "";
}

/**
 * Props: catalog (registry columns), statuses (statusOptions), value (filters
 * array), onChange(filters). Only complete filters are applied.
 */
export function LeadFilterBuilder({ catalog, statuses, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value);
  const ref = useOutside(() => setOpen(false), open);
  useEffect(() => { if (open) setDraft(value.length ? value : []); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const columns = useMemo(() => catalog.filter((c) => c.filterable), [catalog]);
  const groups = useMemo(() => [...new Set(columns.map((c) => c.group))], [columns]);
  const optionsOf = (col) => (col.key === "status" ? statuses.map((s) => ({ value: s.key, label: s.name })) : col.options || []);
  const blank = () => { const col = columns[0]; return { column: col?.key || "", operator: col?.operators?.[0]?.value || "", value: "" }; };
  const set = (i, patch) => setDraft((list) => list.map((f, j) => (j === i ? { ...f, ...patch } : f)));
  const changeColumn = (i, key) => { const col = columns.find((c) => c.key === key); set(i, { column: key, operator: col?.operators?.[0]?.value || "", value: "", value2: undefined }); };
  const ready = draft.filter((f) => complete(f, catalog));
  const apply = () => { onChange(ready); setOpen(false); };

  return (
    <div ref={ref} className="relative">
      <button type="button" aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen((o) => !o)} disabled={!columns.length} className={`flex h-10 items-center gap-2 whitespace-nowrap rounded-full px-4 text-small font-medium ring-1 disabled:opacity-50 ${value.length ? "bg-ink text-paper ring-ink" : "bg-paper text-ink ring-line hover:ring-muted/50"}`}>
        <SlidersHorizontal size={15} strokeWidth={1.75} />
        More filters{value.length ? <span className="tabular-nums text-paper/70">{value.length}</span> : null}
      </button>
      {open && (
        <div role="dialog" aria-label="More filters" className="absolute left-0 top-12 z-30 flex w-[min(640px,calc(100vw-32px))] animate-nav-in flex-col gap-3 rounded-md bg-paper p-4 shadow-md ring-1 ring-line">
          <div className="flex items-center justify-between gap-3"><span className="text-small font-semibold">Show leads where</span>{draft.length > 0 && <button type="button" onClick={() => setDraft([])} className="text-caption font-semibold text-sky-deep hover:underline">Clear all</button>}</div>
          {draft.length === 0 && <p className="text-caption text-muted">No extra filters. Add one to narrow the list by any lead field, like UTM campaign, ad set, score or follow-up date.</p>}
          <ul className="flex max-h-[50vh] flex-col gap-2 overflow-y-auto">
            {draft.map((f, i) => {
              const col = columns.find((c) => c.key === f.column);
              const type = col?.type;
              const opts = col ? optionsOf(col) : [];
              const inputType = type === "number" ? "number" : type === "date" ? "date" : "text";
              return (
                <li key={i} className="flex flex-wrap items-center gap-2 rounded-sm bg-mist/60 p-2 sm:flex-nowrap">
                  {i > 0 && <span className="w-full text-caption font-semibold text-muted sm:w-auto">And</span>}
                  <select aria-label="Field" value={f.column} onChange={(e) => changeColumn(i, e.target.value)} className={`${sel} w-full sm:w-44`}>
                    {groups.map((g) => <optgroup key={g} label={g}>{columns.filter((c) => c.group === g).map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}</optgroup>)}
                  </select>
                  <select aria-label="Condition" value={f.operator} onChange={(e) => set(i, { operator: e.target.value, value: e.target.value === "in" ? [] : "", value2: undefined })} className={`${sel} w-full sm:w-32`}>
                    {(col?.operators || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  {!NO_VALUE.has(f.operator) && (type === "enum" ? (
                    f.operator === "in" ? (
                      <span className="flex min-w-0 flex-1 flex-wrap gap-1">
                        {opts.map((o) => { const on = (f.value || []).includes(o.value); return <button key={o.value} type="button" aria-pressed={on} onClick={() => set(i, { value: on ? f.value.filter((v) => v !== o.value) : [...(f.value || []), o.value] })} className={`h-8 rounded-full px-2.5 text-caption font-medium ${on ? "bg-ink text-paper" : "bg-paper text-ink ring-1 ring-line"}`}>{o.label}</button>; })}
                      </span>
                    ) : (
                      <select aria-label="Value" value={f.value} onChange={(e) => set(i, { value: e.target.value })} className={`${sel} w-full min-w-0 flex-1`}>
                        <option value="">Choose</option>
                        {opts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    )
                  ) : (
                    <span className="flex min-w-0 flex-1 items-center gap-1.5">
                      <input aria-label="Value" type={inputType} value={f.value ?? ""} onChange={(e) => set(i, { value: e.target.value })} placeholder={type === "number" ? "0" : "Value"} className={`${sel} w-full min-w-0 flex-1`} />
                      {f.operator === "between" && <><span className="text-caption text-muted">and</span><input aria-label="Second value" type={inputType} value={f.value2 ?? ""} onChange={(e) => set(i, { value2: e.target.value })} className={`${sel} w-full min-w-0 flex-1`} /></>}
                    </span>
                  ))}
                  <button type="button" aria-label="Remove this filter" onClick={() => setDraft((list) => list.filter((_, j) => j !== i))} className="ml-auto flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-muted hover:bg-paper hover:text-danger"><X size={15} strokeWidth={1.75} /></button>
                </li>
              );
            })}
          </ul>
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line pt-3">
            <button type="button" onClick={() => setDraft((list) => [...list, blank()])} className="flex h-9 items-center gap-1.5 rounded-full px-3 text-small font-semibold hover:bg-mist"><Plus size={15} strokeWidth={1.75} />Add filter</button>
            <div className="flex items-center gap-2">
              {draft.length > ready.length && <span className="text-caption text-muted">{draft.length - ready.length} incomplete, not applied</span>}
              <button type="button" onClick={apply} className="h-9 rounded-full bg-ink px-4 text-small font-semibold text-paper hover:bg-ink/85">Apply</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
